/**
 * renderQrCredentialHero — the real, scannable QR hero the `editorial` and
 * `strata` in-app card variants mount, encoding `ticket.qrToken` through
 * `<schmancy-qr>` (@mhmo91/schmancy's QR-rendering primitive).
 *
 * THE WHITE WELL, AND WHERE IT STOPS: `lg`/`xl` mount a literal `bg-white`
 * plate with pure `#000000` modules — a deliberate maximum-contrast
 * departure from the card's warm-ivory/near-black theme tokens, because
 * door-scan reliability wants true black-on-white and no token in this
 * palette expresses that.
 *
 * `fill` deliberately has NO plate. A white well is ~a padding plus a quiet
 * zone larger than the code on every side, which makes the code's visible
 * ink impossible to align against neighbouring text without compensating
 * for geometry the eye cannot see — and on this card the aligned thing IS
 * the ink (see ticket-inapp.editorial.template.ts). So the code sits
 * straight on the card's own ivory, which is a light ground (~91% L) that
 * serves as the quiet zone, with the surrounding layout asked to keep real
 * space around it. The trade is explicit: slightly less absolute contrast
 * than a true-white well, bought for an alignment that reads correct.
 *
 * TWO SIZING SHAPES OVER ONE ENCODER: `lg`/`xl` mount `<schmancy-qr>` at a
 * fixed pixel edge, for a caller that wants the code sized, not fitted —
 * both shipped cards now fit instead (editorial to its column's width,
 * strata to the height its band's trailing lines leave), so the fixed shape
 * currently has no shipped call site. `fill` instead renders the SAME
 * encoder that component itself wraps — `qrToSvg`, exported for exactly this
 * second consumption shape — as a viewBox-only SVG that scales to whatever
 * box the caller hands it. `<schmancy-qr>` cannot express that: it is an
 * `inline-block` host around a `width:<size>px` div (schmancy's own
 * packages/primitives/src/qr/qr.ts), so a QR asked to FILL a column has no
 * pixel number it could be given. Reaching for the shared encoder here is
 * the sanctioned path rather than a hand-roll — packages/views'
 * order-access.view.ts already draws its share-PNG from the sibling
 * `encodeQr` the same way. A fluid `<schmancy-qr>` mode would let `fill`
 * collapse back onto the component; until it exists this is the one place
 * that gap is absorbed.
 *
 * `preserveAspectRatio` is left at its `xMidYMid meet` default, so a plate
 * a height cap has squeezed out of square letterboxes the code rather than
 * distorting it — a stretched QR is an unscannable QR.
 */
import { qrToSvg } from "@mhmo91/schmancy/qr-encode";
import { type Locale, translate } from "@momo/i18n";
import { html, type TemplateResult } from "lit";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
// Side-effect: registers <schmancy-qr>, the fixed-size primitive the `lg`
// and `xl` branches below mount.
import "@mhmo91/schmancy/qr";

export type QrHeroSize = "lg" | "xl" | "fill";

/** `fill` bakes NO quiet zone into the SVG, so the code's own box IS its ink
 *  box. That is the whole point: a caller aligning the CODE against
 *  neighbouring text then compensates for nothing it cannot see, and both the
 *  code's top and left edges land on the layout's own origin for free. The
 *  quiet zone becomes the caller's to supply as real layout space — the
 *  editorial card spends its identity column's inline padding and its grid
 *  row gap on exactly that. The fixed-edge sizes keep the encoder's own
 *  default, since `<schmancy-qr>` gives them a white plate to sit inside. */
const QR_FILL_QUIET_MODULES = 0;

/** The credential's ink. Black, pinned: a scanner resolves the code against
 *  its ground, so this is a door-level guarantee rather than a colour
 *  choice — every ground this package paints (ticket-inapp.palette.ts) is
 *  pinned for the same reason and proven against this exact value. */
const QR_INK = "#000000";

/** Error-correction level, stated rather than defaulted: how much damage this
 *  code survives at the door is a property of the credential, not something
 *  to inherit silently from the encoder. */
const QR_ECL = "Q" as const;

/** Interior `<schmancy-qr>` pixel size for the fixed-edge sizes — chosen
 *  small enough to clear a treatment's narrowest column at a real phone
 *  viewport, generous enough to still read as the hero. `fill` carries no
 *  entry: its edge is its container's, resolved at layout rather than
 *  chosen here. */
const QR_HERO_PX: Record<"lg" | "xl", number> = {
	lg: 128,
	xl: 208,
};

/** Plate padding — the quiet zone's visual continuation into the white well.
 *  Tighter for `lg`, generous for `xl`. `fill` has no entry because it has no
 *  plate: see QR_FILL_QUIET_MODULES. */
const QR_HERO_PLATE_PADDING: Record<"lg" | "xl", string> = {
	lg: "tkt-qr-plate--lg",
	xl: "tkt-qr-plate--xl",
};

const QR_HERO_STACK_ALIGN: Record<"center" | "start", string> = {
	center: "tkt-align-center",
	start: "tkt-align-start",
};

/**
 * The credential's code alone — no plate, no caption, no stack: the encoded
 * SVG a composition drops into a well it draws itself.
 *
 * WHY THIS EXISTS RATHER THAN A SECOND `qrToSvg` CALL AT THE COMPOSITION: the
 * three arguments below are not per-caller styling, they are the credential's
 * own policy — the ink a scanner must resolve against its ground, the
 * error-correction level that decides how much damage the code survives at
 * the door, and the quiet zone this package supplies as real layout rather
 * than baked pixels. A composition that re-states them inline owns a second,
 * silent copy of a door-level guarantee, free to drift from this one on any
 * edit and answerable to nothing; `QR_ECL`'s own declaration above says as
 * much about being stated rather than inherited. One module encodes the
 * credential for every variant, and a caller chooses only where it sits.
 */
export function renderQrCredentialCode(qrToken: string): TemplateResult {
	return html`${unsafeSVG(
		qrToSvg(qrToken, {
			moduleColor: QR_INK,
			margin: QR_FILL_QUIET_MODULES,
			errorCorrectionLevel: QR_ECL,
		}),
	)}`;
}

export function renderQrCredentialHero(
	locale: Locale,
	qrToken: string,
	size: QrHeroSize,
	align: "center" | "start",
): TemplateResult {
	const qrHint = translate(locale, "documents.ticket.qrHint");
	const caption = html`
    <schmancy-typography type="label" token="xs" transform="uppercase" class="tkt-track-wide tkt-dim">
      ${qrHint}
    </schmancy-typography>
  `;

	if (size === "fill") {
		// Shipped as a co-located <style> rather than Tailwind utilities for the
		// same reason the editorial card's own container query is (see that
		// file's header): aspect-ratio, max-height, and a child-svg selector
		// have no non-arbitrary utility, and an arbitrary utility used only in
		// THIS package is never emitted into a consumer app's Tailwind output —
		// it would render inert in the host that mounts the card.
		return html`
      <style>
        /* Fills the width it is given, stays square, and never outgrows the
           height its row leaves — the three together are what "stretch into
           the space left" means here. */
        .tkt-qr-fill { width: 100%; aspect-ratio: 1; max-height: 100%; }
        .tkt-qr-fill > svg { display: block; width: 100%; height: 100%; }
      </style>
      <div class="tkt-qr-stack ${QR_HERO_STACK_ALIGN[align]}">
        <div role="img" aria-label=${qrHint} class="tkt-qr-fill">
          ${unsafeSVG(
						qrToSvg(qrToken, {
							moduleColor: QR_INK,
							margin: QR_FILL_QUIET_MODULES,
							errorCorrectionLevel: QR_ECL,
						}),
					)}
        </div>
        ${caption}
      </div>
    `;
	}

	return html`
    <div class="tkt-qr-stack ${QR_HERO_STACK_ALIGN[align]}">
      <div class="tkt-qr-plate ${QR_HERO_PLATE_PADDING[size]}">
        <schmancy-qr
          .value=${qrToken}
          .size=${QR_HERO_PX[size]}
          .moduleColor=${QR_INK}
          .label=${qrHint}
        ></schmancy-qr>
      </div>
      ${caption}
    </div>
  `;
}
